/**
 * ========================
 * MENU RENDERER
 * ========================
 *
 * Pure UI component that renders menu from provider configuration.
 *
 * NEVER imports domain providers directly.
 * Queries registry for menu config.
 * Applies preferences from store.
 *
 * RULES:
 * - No domain imports
 * - Pure rendering
 * - Memoized provider lookup
 * - Permission-aware via visible() functions
 */

import { useMemo, cloneElement, isValidElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NavLink, Stack, Text, Badge, rem, Tooltip } from '@mantine/core';
import { getMenuProvider } from '../../../domain/DomainMenuRegistry';
import { MenuItem, MenuSection } from '../types';
import { AccessControl } from '../../../components/security/AccessControl';
import { usePreferencesStore } from '../../../stores/preferencesStore';
import { useUserContext } from '../../../domain/providers/BaseMenuProvider';
import { useAllBadgeStoresRevision } from '../../../domain/badges/useDomainPrimaryBadge';

interface MenuRendererProps {
  domainId: string;
  className?: string;
}

/**
 * Pure menu renderer for domain-specific menus.
 *
 * Queries the domain menu registry for menu configuration,
 * applies user preferences, and renders menu items with
 * permission checks.
 */
export function MenuRenderer({ domainId, className }: MenuRendererProps) {
  const location = useLocation();

  // Get menu preferences from store
  const menuIconOnly = usePreferencesStore((state) => state.menuIconOnly);
  const menuDensity = usePreferencesStore((state) => state.menuDensity);
  const menuShowSectionCounts = usePreferencesStore((state) => state.menuShowSectionCounts);

  // Subscribe to live-count stores so countSelector() outputs stay fresh.
  // countSelector is sync per the menu type contract — the renderer must
  // re-render whenever a backing store changes, otherwise the badge value
  // freezes at first paint. The hook subscribes to every domain's badge
  // store and returns a derived scalar that ticks on any update.
  useAllBadgeStoresRevision();

  // Query registry for provider
  const provider = useMemo(() => {
    const p = getMenuProvider(domainId);
    console.log(`[MenuRenderer] Getting provider for domain "${domainId}":`, p ? 'Found' : 'NOT FOUND');
    return p;
  }, [domainId]);
  const menuConfig = useMemo(() => {
    const config = provider?.getMenuConfig() ?? null;
    console.log(`[MenuRenderer] Menu config for "${domainId}":`, {
      hasSections: !!config?.sections,
      sectionsCount: config?.sections?.length ?? 0,
      hasItems: !!config?.items,
      itemsCount: config?.items?.length ?? 0,
      metadata: config?.metadata
    });
    return config;
  }, [provider, domainId]);

  // Get user context for visibility checks
  const userContext = useUserContext();

  // Active route detection
  const isActive = (path: string) => location.pathname === path;

  // Get density-based padding
  const getDensityPadding = () => {
    switch (menuDensity) {
      case 'compact':
        return rem(6);
      case 'comfortable':
        return rem(12);
      default: // 'regular'
        return rem(8);
    }
  };

  /**
   * Render a single menu item.
   * Applies visibility check, access control, and preference-based styling.
   */
  const renderMenuItem = (item: MenuItem) => {
    // Check visibility function
    const isVisible = item.visible ? item.visible(userContext) : true;
    if (!isVisible) return null;

    // Evaluate count selector if present
    const count = item.countSelector && menuShowSectionCounts ? item.countSelector() : null;

    // Determine if this is an action item (onClick) or a navigation item (path)
    const isActionItem = !!item.onClick;

    const navLinkProps = isActionItem
      ? { onClick: item.onClick, component: 'button' as const }
      : { component: Link, to: item.path || '' };

    const navLink = (
      <NavLink
        {...navLinkProps}
        label={item.label}
        leftSection={
          menuIconOnly ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: rem(40),
              }}
              role="img"
              aria-label={item.label}
            >
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {item.icon && isValidElement(item.icon)
                  ? cloneElement(item.icon as React.ReactElement<any>, {
                      size: 22,
                      'aria-hidden': 'true'
                    })
                  : item.icon}
                {count && (
                  <Badge
                    size="sm"
                    variant="filled"
                    color={item.badgeColor || 'blue'}
                    radius="xl"
                    aria-label={`${count} items`}
                    style={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      minWidth: 18,
                      height: 18,
                      padding: '0 4px',
                      fontSize: '10px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {count}
                  </Badge>
                )}
              </div>
            </div>
          ) : (
            item.icon
          )
        }
        rightSection={
          !menuIconOnly && count ? (
            <Badge size="xs" variant="filled" color={item.badgeColor || 'blue'} radius="sm">
              {count}
            </Badge>
          ) : null
        }
        active={!isActionItem && item.path ? isActive(item.path) : false}
        styles={{
          root: {
            padding: menuIconOnly
              ? `${rem(6)} 0`
              : `${getDensityPadding()} ${rem(8)}`,
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            maxWidth: '100%',
            overflow: 'visible',
            boxSizing: 'border-box',
            position: 'relative',
            margin: 0,
            display: 'flex',
            minHeight: menuIconOnly ? rem(48) : 'auto',
          },
          label: {
            display: menuIconOnly ? 'none' : 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
            maxWidth: '100%',
          },
          body: {
            display: menuIconOnly ? 'none' : 'block',
          },
          section: {
            flexShrink: 0,
            maxWidth: '100%',
            overflow: menuIconOnly ? 'visible' : 'hidden',
          },
        }}
      />
    );

    return (
      <AccessControl
        key={item.id}
        roles={item.requiredRoles}
        permissions={item.requiredPermissions}
        requireAll={item.requireAll}
        strictAccess={item.strictAccess}
      >
        {menuIconOnly ? (
          <Tooltip label={item.label} position="right" withArrow>
            {navLink}
          </Tooltip>
        ) : (
          navLink
        )}
      </AccessControl>
    );
  };

  /**
   * Render a menu section with header and items.
   * Sections are non-collapsible headers as per user requirements.
   */
  const renderSection = (section: MenuSection, index: number) => {
    // Check section visibility
    const isVisible = section.visible ? section.visible(userContext) : true;
    if (!isVisible) return null;

    // Evaluate section count selector if present
    const sectionCount =
      menuShowSectionCounts && section.countSelector ? section.countSelector() : null;

    return (
      <Stack
        key={section.id}
        gap={menuIconOnly ? 0 : "xs"}
        mt={index > 0 ? (menuIconOnly ? 0 : 'md') : 0}
        align="flex-start"
        style={{
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* Section header - only show if not in icon-only mode */}
        {!menuIconOnly && (
          <Text
            size="10px"
            c="gray.6"
            fw={500}
            tt="uppercase"
            ta="left"
            opacity={0.7}
            style={{
              width: '100%',
              maxWidth: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              overflow: 'hidden',
              boxSizing: 'border-box',
              paddingRight: rem(4),
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {section.label}
            </span>
            {sectionCount !== null && (
              <Badge size="xs" variant="light" color="gray" style={{ flexShrink: 0 }}>
                {sectionCount}
              </Badge>
            )}
          </Text>
        )}

        {/* Section items */}
        <Stack gap={menuIconOnly ? 0 : "xs"} style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
          {section.items.map(renderMenuItem)}
        </Stack>
      </Stack>
    );
  };

  // If provider not found or no config, show fallback
  if (!provider || !menuConfig) {
    return (
      <Stack p="md" gap="xs">
        <Text size="sm" c="dimmed">
          No menu available for domain: {domainId}
        </Text>
      </Stack>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Stack
        gap="xs"
        p={menuIconOnly ? 0 : rem(12)}
        px={menuIconOnly ? rem(4) : rem(12)}
        className={className}
        style={{
          width: '100%',
          overflow: 'hidden',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Render sections if provided */}
        {menuConfig.sections && menuConfig.sections.map(renderSection)}

        {/* Render flat items if provided */}
        {menuConfig.items && menuConfig.items.map(renderMenuItem)}

        {/* Debug info in development */}
        {import.meta.env.DEV && menuConfig.metadata && (
          <Text size="10px" c="dimmed" opacity={0.5} mt="md">
            {menuConfig.metadata.description} v{menuConfig.metadata.version}
          </Text>
        )}
      </Stack>
    </div>
  );
}
